import { CheckCircleFilled } from '@ant-design/icons';
import { PageController } from '@client/components/_core/layout/PageController';
import { Button } from '@client/components/_ui/data-entry/ButtonModern';
import { PhoneNumberInput } from '@client/components/_ui/data-entry/PhoneNumberInput';
import { errorMessage } from '@client/components/_ui/feedback/error-message/ErrorMessage';
import { message } from '@client/components/_ui/feedback/message';
import { modal } from '@client/components/_ui/feedback/modal';
import { useUser } from '@client/hooks/business/user/useUser';
import { useFeatureFlag } from '@client/hooks/useFeatureFlag';
import { useAuthStore } from '@client/stores/AuthStore';
import type { AuthStoreUser } from '@client/stores/AuthStore/types';
import { trpc } from '@client/trpc/client';
import { debounce } from '@client/utils/common';
import { AUTH0_CONNECTIONS } from '@shared/constants';
import { createFileRoute } from '@tanstack/react-router';
import { Avatar, Card, Form, Input, Typography } from 'antd';
import { useEffect, useState } from 'react';

export const Route = createFileRoute('/console/$orgId/user-settings/profile')({
    staticData: {
        metadata: {
            title: 'Profile',
        },
        ui: {
            widthVariant: 'compact',
        },
    },
    component: RouteComponent,
});

function RouteComponent() {
    return (
        <PageController route={Route}>
            <ProfileSettings />
        </PageController>
    );
}

type User = Pick<AuthStoreUser, 'fullName' | 'email' | 'provider' | 'phone'>;

const FormItem = Form.Item<User>;

function ProfileSettings() {
    const user = useUser();
    const enableSsoMigration = useFeatureFlag('enable-sso-migration');
    const allowMigrationToSsoMutation = trpc.user.allowMigrationToSso.useMutation();
    const updateMeMutation = trpc.user.updateMe.useMutation();
    const { data } = trpc.user.getUser.useQuery({
        id: user.id,
        orgId: user.orgId,
    });
    const allowMigrationToSso = useAuthStore((state) => state.allowMigrationToSso);
    const reloadUser = useAuthStore((state) => state.reloadUser);

    const [isMigrationToSsoAllowed, setIsMigrationToSsoAllowed] = useState(false);
    const [form] = Form.useForm();
    const isSsoProvider = user.isSsoActivated || user.provider === 'curium-ad';

    useEffect(() => {
        if (data) {
            form.setFieldsValue({
                ...data,
                provider: Object.entries(AUTH0_CONNECTIONS).find(
                    ([, value]) =>
                        (value.name === 'curium-ad' && isSsoProvider) ||
                        (data?.provider === value.name && !isSsoProvider),
                )?.[1].label,
            });
        }
    }, [data]);

    const onFinish = debounce((values: User) => {
        message.loading('Saving...');

        form.setFields([
            {
                name: 'phone',
                errors: [],
            },
        ]);

        updateMeMutation.mutate(
            {
                set: {
                    phone: values.phone,
                },
            },
            {
                onSuccess() {
                    reloadUser();
                    message.success('Saved.');
                },
                onError(error) {
                    if (error.data?.errorId === 'user.phoneAlreadyTaken') {
                        message.destroy();

                        form.setFields([
                            {
                                name: 'phone',
                                errors: ['This phone number is already in use.'],
                            },
                        ]);

                        return;
                    }

                    errorMessage.show(error);
                },
            },
        );
    });

    return (
        <Card>
            <Form form={form} labelCol={{ span: 6 }} wrapperCol={{ span: 18 }} onFinish={onFinish}>
                <Avatar src={user.photoUrl} size={96} className="mx-auto mb-4 block" />
                <FormItem label="Name" name="fullName">
                    <Input disabled />
                </FormItem>
                <FormItem label="E-mail" name="email">
                    <Input disabled />
                </FormItem>
                <FormItem label="Phone" name="phone">
                    <PhoneNumberInput />
                </FormItem>
                <FormItem label="Authentication method" name="provider">
                    <Input
                        disabled
                        addonAfter={
                            !isSsoProvider &&
                            enableSsoMigration && (
                                <Button
                                    type="primary"
                                    size="small"
                                    className="w-28.5"
                                    disabled={isMigrationToSsoAllowed}
                                    icon={isMigrationToSsoAllowed && <CheckCircleFilled />}
                                    onClick={async () => {
                                        modal.confirm({
                                            title: 'Migrate to Single Sign-On',
                                            content: (
                                                <>
                                                    <Typography.Paragraph>
                                                        You are about to switch to Single Sign-On (SSO). After this, you
                                                        will be able to use your account to sign in via SSO. Password
                                                        sign in will be disabled.
                                                    </Typography.Paragraph>
                                                    <Typography.Paragraph>
                                                        To sign in with SSO, you will need to log out and then click the{' '}
                                                        <Typography.Text strong>
                                                            "Continue with Azure AD"
                                                        </Typography.Text>{' '}
                                                        button when signing in.
                                                    </Typography.Paragraph>
                                                    <Typography.Paragraph>
                                                        Are you sure you want to proceed?
                                                    </Typography.Paragraph>
                                                </>
                                            ),
                                            onOk() {
                                                if (user.allowMigrationToSso) {
                                                    setIsMigrationToSsoAllowed(true);
                                                    message.warning('Migration to SSO is already allowed.');
                                                    return;
                                                }

                                                allowMigrationToSsoMutation.mutate(undefined, {
                                                    onSuccess() {
                                                        allowMigrationToSso();
                                                        setIsMigrationToSsoAllowed(true);
                                                        message.success(
                                                            'We have received your request to migrate your login to SSO. If your email domain is SSO enabled, the migration will occur when you next login. If not, you will continue to to be registered with username and password.',
                                                            4,
                                                        );
                                                    },
                                                });
                                            },
                                        });
                                    }}
                                >
                                    {!isMigrationToSsoAllowed && 'Migrate to SSO'}
                                </Button>
                            )
                        }
                    />
                </FormItem>
                <div className="flex justify-end">
                    <Button htmlType="submit" type="primary" loading={updateMeMutation.isPending}>
                        Save
                    </Button>
                </div>
            </Form>
        </Card>
    );
}
